import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { API_BASE_URL } from "../../config";
import { FiEye, FiEyeOff, FiLock, FiMail } from "react-icons/fi";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";

const MySwal = withReactContent(Swal);

const LoginBox: React.FC = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [loginType, setLoginType] = useState<"user" | "admin">("user");

  useEffect(() => {
    const savedEmail = localStorage.getItem("userEmail");
    const savedPassword = localStorage.getItem("userPassword");
    const savedRemember = localStorage.getItem("rememberMe") === "true";

    if (savedEmail && savedPassword && savedRemember) {
      setEmail(savedEmail);
      setPassword(savedPassword);
      setRememberMe(true);
    }

    if (localStorage.getItem("isLoggedIn") === "true") {
      navigate("/", { replace: true });
    }
  }, [navigate]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    if (!email || !password) {
      setError("Please enter both email and password.");
      setIsLoading(false);
      return;
    }

    const endpoint =
      loginType === "admin" ? "/admin/login" : "/auth/login";

    try {
      const res = await fetch(`${API_BASE_URL}${endpoint}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });

      const result = await res.json();

      if (!res.ok) {
        const errMsg = result.error || "Login failed.";

        if (res.status === 403 && errMsg.includes("confirm")) {
          await MySwal.fire({
            icon: "info",
            title: "Email Not Confirmed",
            text: "Please confirm your email address to log in.",
            confirmButtonText: "OK",
            confirmButtonColor: "#6366f1",
            customClass: {
              confirmButton:
                "bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded",
            },
            buttonsStyling: false,
          });
        } else {
          await MySwal.fire({
            icon: "error",
            title: "Login Failed",
            text: errMsg,
            confirmButtonColor: "#6366f1",
            customClass: {
              confirmButton:
                "bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded",
            },
            buttonsStyling: false,
          });
        }
        return;
      }

      // ✅ Save session
      //localStorage.setItem("isLoggedIn", "true");
      localStorage.setItem("userName", result.name);
      localStorage.setItem("loginType", loginType);
      if (loginType === "admin") {
        localStorage.setItem("isAdminLoggedIn", "true");
        localStorage.setItem("adminToken", result.token);
        navigate("/admin/dashboard", { replace: true });
      } else {
        localStorage.setItem("isLoggedIn", "true");
        localStorage.setItem("authToken", result.token);
        navigate("/", { replace: true });

      }

      if (rememberMe && loginType === "user") {
        localStorage.setItem("userEmail", email);
        localStorage.setItem("userPassword", password);
        localStorage.setItem("rememberMe", "true");
      }

    } catch (err) {
      console.error("Login error:", err);
      await MySwal.fire({
        icon: "error",
        title: "Server Error",
        text: "Something went wrong. Please try again later.",
        confirmButtonColor: "#6366f1",
        buttonsStyling: false,
        customClass: {
          confirmButton:
            "bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded",
        },
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 to-blue-50 pt-20">
      <div className="w-full max-w-md bg-white p-6 rounded-lg shadow-lg">
        {/* Tabs */}
        <div className="flex justify-center mb-4">
          <button
            className={`px-4 py-2 font-semibold rounded-t-md ${loginType === "user"
              ? "bg-indigo-600 text-white"
              : "bg-gray-100 text-gray-600"
              }`}
            onClick={() => setLoginType("user")}
          >
            User Login
          </button>
          <button
            className={`px-4 py-2 font-semibold rounded-t-md ${loginType === "admin"
              ? "bg-indigo-600 text-white"
              : "bg-gray-100 text-gray-600"
              }`}
            onClick={() => setLoginType("admin")}
          >
            Admin Login
          </button>
        </div>

        <h2 className="text-2xl font-bold text-center text-indigo-700 mb-6">
          Login to Quizora ({loginType === "user" ? "User" : "Admin"})
        </h2>

        {error && (
          <div className="bg-red-50 border-l-4 border-red-500 p-3 text-red-700 text-sm mb-4 rounded">
            {error}
          </div>
        )}

        <form className="space-y-4" onSubmit={handleLogin}>
          <div className="relative">
            <FiMail className="absolute left-3 top-3 text-gray-400" />
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Email"
              className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500"
              required
            />
          </div>

          <div className="relative">
            <FiLock className="absolute left-3 top-3 text-gray-400" />
            <input
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              className="pl-10 pr-10 py-2 w-full border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500"
              required
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-2.5 text-gray-500"
            >
              {showPassword ? <FiEyeOff /> : <FiEye />}
            </button>
          </div>

          {/* Remember Me for Users only */}
          {loginType === "user" && (
            <div className="flex justify-between items-center text-sm">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="accent-indigo-600"
                />
                Remember me
              </label>
              <button
                type="button"
                onClick={() => navigate("/forgetpassword")}
                className="text-indigo-600 hover:underline"
              >
                Forgot password?
              </button>
            </div>
          )}

          <button
            type="submit"
            disabled={isLoading}
            className={`w-full py-2 text-white font-semibold rounded transition 
              ${isLoading ? "bg-indigo-400" : "bg-indigo-600 hover:bg-indigo-700"}`}
          >
            {isLoading ? "Logging in..." : "Login"}
          </button>

          {loginType === "user" && (
            <p className="text-sm text-center text-gray-600">
              Don’t have an account?{" "}
              <button
                type="button"
                className="text-indigo-600 hover:underline font-medium"
                onClick={() => navigate("/register")}
              >
                Register now
              </button>
            </p>
          )}
        </form>
      </div>
    </div>
  );
};

export default LoginBox;
